/**
 * Script loading utility
 * @module lib/scriptLoader
 */

const { logDebug, logError } = require('./logger');

// Load state lives on the <script> element, so the core SDK and each lazy
// bundle (all with their own copy of this module) agree on one tag. Without
// it, a second loadScript for a preloaded URL resolved before the script had
// run, and the first auction could go out before Prebid existed.
const STATE_ATTRIBUTE = 'data-milla-load-state';
const pending = new WeakMap();

function waitForScript(script, url) {
  if (pending.has(script)) return pending.get(script);
  const promise = new Promise((resolve, reject) => {
    const state = script.getAttribute(STATE_ATTRIBUTE);
    if (state === 'loaded') {
      resolve();
      return;
    }
    if (state === 'error') {
      reject(new Error(`Failed to load script: ${url}`));
      return;
    }
    const cleanup = () => {
      script.removeEventListener('load', onLoad);
      script.removeEventListener('error', onError);
    };
    const onLoad = () => {
      cleanup();
      script.setAttribute(STATE_ATTRIBUTE, 'loaded');
      logDebug('script:loaded', { url });
      resolve();
    };
    const onError = () => {
      cleanup();
      script.setAttribute(STATE_ATTRIBUTE, 'error');
      const err = new Error(`Failed to load script: ${url}`);
      logError(err);
      reject(err);
    };
    script.addEventListener('load', onLoad);
    script.addEventListener('error', onError);
  });
  pending.set(script, promise);
  return promise;
}

function loadScript(url) {
  if (typeof document === 'undefined') {
    return Promise.reject(new Error('document is not available'));
  }

  const existing = Array.from(document.getElementsByTagName('script')).find(
    (s) => s.src && s.src.indexOf(url) !== -1
  );
  if (existing) {
    // A tag the page added itself carries no state and is treated as loaded,
    // as before; one this SDK injected resolves once it has actually run.
    if (!existing.hasAttribute(STATE_ATTRIBUTE)) {
      logDebug('script:exists', { url });
      return Promise.resolve();
    }
    return waitForScript(existing, url);
  }

  const script = document.createElement('script');
  script.type = 'text/javascript';
  script.setAttribute(STATE_ATTRIBUTE, 'loading');
  const loaded = waitForScript(script, url);
  logDebug('script:inject', { url });
  script.src = url;
  document.getElementsByTagName('head')[0].appendChild(script);
  return loaded;
}

// Resolves once the script has run or after `ms`, whichever is first; a load
// error still rejects. For scripts the caller can start without (Prebid: the
// first request goes GPT-only), so a stalled download never holds the page.
function loadScriptWithin(url, ms) {
  let timer;
  return Promise.race([
    loadScript(url),
    new Promise((resolve) => { timer = setTimeout(resolve, ms); })
  ]).finally(() => clearTimeout(timer));
}

module.exports = { loadScript, loadScriptWithin };
